import React, {
  useMemo,
  useState,
} from "react";

function PlatformPopup({
  selectedPlatform,
  selectedProducts,
  closePopup,
}) {

  // =====================================================
  // STATES
  // =====================================================

  const [searchTerm, setSearchTerm] =
    useState("");

  const [sortField, setSortField] =
    useState("master_product");

  const [sortOrder, setSortOrder] =
    useState("asc");

  // =====================================================
  // CLOSE IF EMPTY
  // =====================================================

  if (!selectedPlatform) {

    return null;
  }

  // =====================================================
  // SUMMARY
  // =====================================================

  const totalProducts =
    selectedProducts.length;

  const avgGap =
    totalProducts > 0

      ? (

          selectedProducts.reduce(
            (sum, item) =>

              sum +

              (
                Number(
                  item.price_difference_pct
                ) || 0
              ),

            0
          ) / totalProducts

        ).toFixed(2)

      : 0;

  // =====================================================
  // SORT HANDLER
  // =====================================================

  const handleSort = (
    field
  ) => {

    if (sortField === field) {

      setSortOrder(

        sortOrder === "asc"

          ? "desc"

          : "asc"
      );

    } else {

      setSortField(field);

      setSortOrder("asc");
    }
  };

  // =====================================================
  // FILTER + SORT
  // =====================================================

  const processedProducts =
    useMemo(() => {

      let filtered = [
        ...selectedProducts,
      ];

      // ===============================================
      // SEARCH
      // ===============================================

      if (searchTerm) {

        filtered =
          filtered.filter(
            (item) =>

              (
                item.master_product ||
                ""
              )

                .toLowerCase()

                .includes(
                  searchTerm.toLowerCase()
                )
          );
      }

      // ===============================================
      // SORT
      // ===============================================

      filtered.sort((a, b) => {

        let valueA =
          a[sortField];

        let valueB =
          b[sortField];

        if (
          typeof valueA ===
          "string"
        ) {

          valueA =
            valueA.toLowerCase();

          valueB =
            valueB.toLowerCase();
        }

        if (valueA < valueB) {

          return sortOrder ===
            "asc"

            ? -1

            : 1;
        }

        if (valueA > valueB) {

          return sortOrder ===
            "asc"

            ? 1

            : -1;
        }

        return 0;
      });

      return filtered;

    }, [
      selectedProducts,
      searchTerm,
      sortField,
      sortOrder,
    ]);

  // =====================================================
  // PRICE FORMAT
  // =====================================================

  const formatPrice = (
    value
  ) => {

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {

      return "-";
    }

    return `₹ ${value}`;
  };

  // =====================================================
  // DIFFERENCE BADGE
  // =====================================================

  const getDifferenceBadge = (
    diff
  ) => {

    const value =
      Number(diff) || 0;

    if (value <= 0) {

      return (
        <span className="badge badge-up">

          {value}%

        </span>
      );
    }

    return (
      <span className="badge badge-down">

        +{value}%

      </span>
    );
  };

  const hasResults =
    processedProducts.length > 0;

  // =====================================================
  // RENDER
  // =====================================================

  return (

    <div className="popup-overlay">

      <div className="popup-content large-popup">

        {/* =========================================
            CLOSE
        ========================================= */}

        <button
          className="close-btn"
          onClick={closePopup}
        >
          ✕
        </button>

        {/* =========================================
            HEADER
        ========================================= */}

        <div className="popup-header">

          <div>

            <h2 className="popup-title">

              {selectedPlatform}
              {" "}
              Competitive Insights

            </h2>

            <p className="popup-subtitle">

              Products where
              {" "}
              {selectedPlatform}
              {" "}
              has pricing advantage

            </p>

          </div>

        </div>

        {/* =========================================
            SUMMARY
        ========================================= */}

        <div className="popup-summary-row">

          <div className="badge badge-up">

            Winning Products:
            {" "}
            {totalProducts}

          </div>

          <div className="badge badge-amber">

            Avg Gap:
            {" "}
            {avgGap}%

          </div>

        </div>

        {/* =========================================
            SEARCH
        ========================================= */}

        <div className="popup-search-row">

          <input
            type="text"
            placeholder="Search product..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
            className="popup-search"
          />

        </div>

        {/* =========================================
            TABLE
        ========================================= */}

        <div className="table-wrapper popup-table-wrapper">

          {hasResults ? (
            <table className="modern-table">

              {/* =====================================
                  HEADER
              ===================================== */}

              <thead>

              <tr>

                <th
                  onClick={() =>
                    handleSort(
                      "master_product"
                    )
                  }
                >

                  Product

                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "master_category"
                    )
                  }
                >

                  Category

                </th>

                <th
                  onClick={() =>
                    handleSort(
                      "quantity_value"
                    )
                  }
                >

                  Quantity

                </th>

                <th>

                  Unit

                </th>

                <th>

                  F2B

                </th>

                <th>

                  Blinkit

                </th>

                <th>

                  Zepto

                </th>

                <th>

                  BigBasket

                </th>

                <th>

                  Difference

                </th>

                <th>

                  Cheapest

                </th>

              </tr>

            </thead>

            {/* =====================================
                BODY
            ===================================== */}

            <tbody>

              {processedProducts.map(
                (
                  item,
                  index
                ) => {

                  const cheapest =
                    (
                      item.cheapest_platform ||
                      ""
                    )
                      .toLowerCase();

                  return (

                    <tr key={index}>

                      {/* PRODUCT */}

                      <td className="product-name">

                        {
                          item.master_product
                        }

                      </td>

                      {/* CATEGORY */}

                      <td>

                        {
                          item.master_category ||
                          "-"
                        }

                      </td>

                      {/* QUANTITY */}

                      <td>

                        {
                          item.quantity_value ||
                          "-"
                        }

                      </td>

                      {/* UNIT */}

                      <td>

                        {
                          item.quantity_unit ||
                          "-"
                        }

                      </td>

                      {/* F2B */}

                      <td className="price-cell">

                        {formatPrice(
                          item.farm2bag_price
                        )}

                      </td>

                      {/* BLINKIT */}

                      <td className="price-cell">

                        {formatPrice(
                          item.blinkit_price
                        )}

                      </td>

                      {/* ZEPTO */}

                      <td className="price-cell">

                        {formatPrice(
                          item.zepto_price
                        )}

                      </td>

                      {/* BIGBASKET */}

                      <td className="price-cell">

                        {formatPrice(
                          item.bigbasket_price
                        )}

                      </td>

                      {/* DIFFERENCE */}

                      <td>

                        {getDifferenceBadge(
                          item.price_difference_pct
                        )}

                      </td>

                      {/* CHEAPEST */}

                      <td>

                        <span
                          className={
                            cheapest ===
                            "farm2bag"

                              ? "badge badge-up"

                              : cheapest

                              ? "badge badge-down"

                              : "badge badge-amber"
                          }
                        >

                          {
                            item.cheapest_platform ||
                            "-"
                          }

                        </span>

                      </td>

                    </tr>

                  );
                }
              )}

            </tbody>

            </table>
          ) : (
            <div className="popup-empty">
              No products match this platform or
              current filters.
            </div>
          )}

        </div>

      </div>

    </div>
  );
}

export default PlatformPopup;